import React, { useEffect, useRef, useState } from "react";
import "./ProjectsGallery.scss";
import { rootsCaseStudySlides } from "./rootsCaseStudy";
import { bloomreachBannerSlide } from "./bloomreachBannerSlide";

const ProjectCard = ({ project, onOpen, compact = false, revealDelay = 0 }) => {
  const visibleTags = project.techStack.slice(0, compact ? 2 : 4);

  return (
    <article
      className={`project-card reveal ${compact ? "project-card--compact" : ""}`}
      style={{ "--reveal-delay": `${revealDelay}ms` }}
    >
      <div className="project-card__image-wrap">
        {project.status && <span className="project-card__status">{project.status}</span>}
        <img src={project.img} alt={`${project.title} preview`} />
      </div>
      <div className="project-card__content">
        <h3>{project.title}</h3>
        <p>{project.cardInfo || project.info}</p>
        <ul className="project-tags" aria-label={`${project.title} technology`}>
          {visibleTags.map((tag) => (
            <li key={tag}>{tag}</li>
          ))}
        </ul>
        <button
          className="key-button project-card__button"
          type="button"
          onClick={(event) => onOpen(project, event.currentTarget)}
        >
          {compact ? "Learn More" : "View Case Study"}
        </button>
      </div>
    </article>
  );
};

const ProjectsComponent = ({ projects }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const closeButtonRef = useRef(null);
  const modalRef = useRef(null);
  const slideFrameRef = useRef(null);
  const triggerRef = useRef(null);

  const featuredProjects = projects.filter((project) => project.group === "featured");
  const earlierProjects = projects.filter((project) => project.group === "earlier");

  const openModal = (project, trigger) => {
    triggerRef.current = trigger;
    setSelectedProject(project);
    setActiveSlide(0);
  };

  const closeModal = () => {
    setSelectedProject(null);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  useEffect(() => {
    if (!selectedProject) {
      return undefined;
    }

    closeButtonRef.current?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeModal();
      }

      if (event.key === "ArrowLeft") {
        setActiveSlide((current) => Math.max(current - 1, 0));
      }

      if (event.key === "ArrowRight") {
        const lastSlide = selectedProject.id === "roots-ecommerce"
          ? rootsCaseStudySlides.length - 1
          : selectedProject.id === "roots-refer-a-friend" ? 5 : 4;
        setActiveSlide((current) => Math.min(current + 1, lastSlide));
      }

      if (event.key === "Tab") {
        const focusable = modalRef.current?.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable?.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedProject]);

  useEffect(() => {
    if (slideFrameRef.current) {
      slideFrameRef.current.scrollTop = 0;
    }
  }, [activeSlide]);

  const defaultCaseStudySlides = selectedProject
    ? [
        {
          label: "Overview",
          content: (
            <section className="case-study-slide" aria-labelledby="case-slide-overview">
              <h4 id="case-slide-overview">Overview</h4>
              <p>{selectedProject.overview || selectedProject.info}</p>
              {selectedProject.overview &&
                selectedProject.info !== selectedProject.overview && (
                <p>{selectedProject.info}</p>
              )}
            </section>
          ),
        },
        {
          label: "My Role",
          content: (
            <section className="case-study-slide" aria-labelledby="case-slide-role">
              <h4 id="case-slide-role">My Role</h4>
              <p>{selectedProject.role}</p>
            </section>
          ),
        },
        {
          label: "Highlights",
          content: (
            <section className="case-study-slide" aria-labelledby="case-slide-highlights">
              <h4 id="case-slide-highlights">Highlights</h4>
              <ul>
                {selectedProject.highlights.map((highlight) => (
                  <li key={highlight}>{highlight}</li>
                ))}
              </ul>
            </section>
          ),
        },
        {
          label: "Tech",
          content: (
            <section className="case-study-slide" aria-labelledby="case-slide-tech">
              <h4 id="case-slide-tech">Tech</h4>
              <ul className="project-tags project-tags--modal">
                {selectedProject.techStack.map((tag) => (
                  <li key={tag}>{tag}</li>
                ))}
              </ul>
            </section>
          ),
        },
        {
          label: "Screenshots",
          content: (
            <section className="case-study-slide" aria-labelledby="case-slide-screenshots">
              <h4 id="case-slide-screenshots">Screenshots</h4>
              <img
                className="case-study-slide__image"
                src={selectedProject.img}
                alt={`${selectedProject.title} screenshot`}
              />
              {selectedProject.link && (
                <a
                  className="key-button project-card__button project-card__button--link"
                  href={selectedProject.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit Site
                </a>
              )}
            </section>
          ),
        },
      ]
    : [];
  const caseStudySlides = selectedProject?.id === "roots-ecommerce"
    ? rootsCaseStudySlides
    : selectedProject?.id === "roots-refer-a-friend"
      ? [...defaultCaseStudySlides, bloomreachBannerSlide]
      : defaultCaseStudySlides;

  const goToPreviousSlide = () => {
    setActiveSlide((current) => Math.max(current - 1, 0));
  };

  const goToNextSlide = () => {
    setActiveSlide((current) => Math.min(current + 1, caseStudySlides.length - 1));
  };

  return (
    <section className="projects" id="projects">
      <div className="projects__intro reveal">
        <p className="projects__eyebrow">E-commerce and product work</p>
        <h1 className="contact-nameheaderdesktop">Featured Work</h1>
      </div>

      <div className="projects-grid">
        {featuredProjects.map((project, index) => (
          <ProjectCard
            key={project.id}
            project={project}
            onOpen={openModal}
            revealDelay={(index % 2) * 90}
          />
        ))}
      </div>

      <div className="earlier-work">
        <h2 className="reveal">More Projects</h2>
        <div className="earlier-work__grid">
          {earlierProjects.map((project, index) => (
            <ProjectCard
              key={project.id}
              project={project}
              onOpen={openModal}
              compact
              revealDelay={(index % 3) * 70}
            />
          ))}
        </div>
      </div>

      {selectedProject && (
        <div className="modal-overlay" onMouseDown={closeModal}>
          <div
            className="modal-content"
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="project-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              className="close"
              type="button"
              aria-label="Close project details"
              onClick={closeModal}
              ref={closeButtonRef}
            >
              &times;
            </button>
            <div className="modal-content__details">
              {selectedProject.status && (
                <span className="project-card__status project-card__status--modal">
                  {selectedProject.status}
                </span>
              )}
              <h3 id="project-modal-title">{selectedProject.title}</h3>

              <nav className="case-study-tabs" aria-label="Case study sections">
                {caseStudySlides.map((slide, index) => (
                  <button
                    key={slide.label}
                    type="button"
                    className={`case-study-tab ${
                      activeSlide === index ? "case-study-tab--active" : ""
                    }`}
                    aria-current={activeSlide === index ? "step" : undefined}
                    onClick={() => setActiveSlide(index)}
                  >
                    {slide.label}
                  </button>
                ))}
              </nav>

              <div
                className="case-study-frame"
                aria-live="polite"
                aria-label="Case study slide content"
                tabIndex={0}
                ref={slideFrameRef}
              >
                <div className="case-study-frame__meta">
                  <span>
                    {activeSlide + 1} / {caseStudySlides.length}
                  </span>
                  <strong>{caseStudySlides[activeSlide].label}</strong>
                </div>
                <div className="case-study-slide-shell" key={activeSlide}>
                  {caseStudySlides[activeSlide].content}
                </div>
              </div>

              <div className="case-study-controls">
                <button
                  className="key-button case-study-control"
                  type="button"
                  onClick={goToPreviousSlide}
                  disabled={activeSlide === 0}
                >
                  Previous
                </button>

                <div className="case-study-progress" aria-label="Case study progress">
                  {caseStudySlides.map((slide, index) => (
                    <button
                      key={slide.label}
                      type="button"
                      className={`case-study-progress__dot ${
                        activeSlide === index ? "case-study-progress__dot--active" : ""
                      }`}
                      aria-label={`Go to ${slide.label}`}
                      aria-pressed={activeSlide === index}
                      onClick={() => setActiveSlide(index)}
                    />
                  ))}
                </div>

                <button
                  className="key-button case-study-control"
                  type="button"
                  onClick={goToNextSlide}
                  disabled={activeSlide === caseStudySlides.length - 1}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProjectsComponent;
